// apps/dashboard/src/hooks/use-billing.ts
import { api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ── DTOs ──────────────────────────────────────────────────────────────────────
export type PlanCode = 'FREE' | 'STARTER' | 'PRO' | 'BUSINESS' | 'ENTERPRISE';
export type BillingCycle = 'MONTHLY' | 'ANNUAL';
export type SubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'SUSPENDED' | 'CANCELLED';

export interface SubscriptionDto {
  id: string;
  tenantId: string;
  planCode: PlanCode;
  billingCycle: BillingCycle;
  status: SubscriptionStatus;
  trialEndsAt: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  gracePeriodEndsAt: string | null;
  pendingDowngradePlan: PlanCode | null;
  quotas: {
    messages: number;
    chatbotSessions: number;
    agents: number;
    numbers: number;
    hasOverage: boolean;
  };
}

export interface CurrentUsageDto {
  period: string;
  quota: { messages: number; chatbotSessions: number; agents: number; numbers: number };
  used: { messages: number; chatbotSessions: number };
  percentUsed: number;
  chatbotPercentUsed: number;
  nearLimit: boolean;
  overLimit: boolean;
  metaCostBrl: number;
}

export interface ChatbotUsageDto {
  period: string;
  metrics: { type: string; count: number }[];
  containmentRate: number;
}

export interface UsageBreakdownChannelDto {
  channel: string;
  eventType: string;
  messages: number;
  percentOfQuota: number;
}

export interface UsageBreakdownDto {
  period: string;
  breakdown: UsageBreakdownChannelDto[];
  total: number;
}

export interface ProjectedCostDto {
  period: string;
  daysElapsed: number;
  daysTotal: number;
  usedMessages: number;
  projectedMessages: number;
  quota: number;
  projectedOverageMessages: number;
  projectedOverageBrl: number;
  hasOverage: boolean;
}

// ── Hooks ─────────────────────────────────────────────────────────────────────
export function useBillingSubscription() {
  return useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => api.get<SubscriptionDto>('/billing/subscription'),
  });
}

export function useCurrentUsage() {
  return useQuery({
    queryKey: ['billing', 'usage', 'current'],
    queryFn: () => api.get<CurrentUsageDto>('/billing/usage/current'),
  });
}

export function useUsageBreakdown() {
  return useQuery({
    queryKey: ['billing', 'usage', 'breakdown'],
    queryFn: () => api.get<UsageBreakdownDto>('/billing/usage/breakdown'),
  });
}

export function useChatbotUsage(period?: string) {
  return useQuery({
    queryKey: ['billing', 'usage', 'chatbot', period],
    queryFn: () =>
      api.get<ChatbotUsageDto>(`/billing/usage/chatbot${period ? `?period=${period}` : ''}`),
  });
}

export function useProjectedCost() {
  return useQuery({
    queryKey: ['billing', 'usage', 'projected-cost'],
    queryFn: () => api.get<ProjectedCostDto>('/billing/usage/projected-cost'),
  });
}

export function useUpgradePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planCode: PlanCode) =>
      api.post<SubscriptionDto>('/billing/subscription/upgrade', { planCode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export function useDowngradePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planCode: PlanCode) =>
      api.post<SubscriptionDto>('/billing/subscription/downgrade', { planCode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

// ── Rate limits ───────────────────────────────────────────────────────────────
export interface RateLimitsDto {
  window: string;
  limit: number;
  used: number;
  remaining: number;
  pct: number;
  resetAt: string;
  alert: boolean;
}

export function useRateLimits() {
  return useQuery<{ rateLimits: RateLimitsDto }>({
    queryKey: ['me', 'rate-limits'],
    queryFn: () => api.get('/me/rate-limits'),
    refetchInterval: 60_000,
  });
}

// ── Onboarding status ─────────────────────────────────────────────────────────
export type OnboardingStep =
  | 'CHANNEL'
  | 'INBOX'
  | 'AGENT'
  | 'IMPORT_CONTACTS'
  | 'TEST_MESSAGE'
  | 'DONE';

export interface OnboardingStatusDto {
  currentStep: OnboardingStep;
  completedSteps: OnboardingStep[];
  completedAt?: string;
  isDismissed?: boolean;
}

export function useOnboardingStatus() {
  return useQuery<{ onboarding: OnboardingStatusDto }>({
    queryKey: ['me', 'onboarding'],
    queryFn: () => api.get('/me/onboarding-status'),
  });
}

// ── PIX In-chat Hooks ──────────────────────────────────────────────────────────
export interface PixChargeDto {
  id: string;
  tenantId: string;
  conversationId: string;
  amountCents: number;
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
  asaasPaymentId: string;
  qrCodeBase64?: string;
  brCode?: string;
  description?: string;
  paidAt?: string;
  expiresAt: string;
  createdAt: string;
}

export function useConversationPixCharges(conversationId: string) {
  return useQuery({
    queryKey: ['billing', 'pix', conversationId],
    queryFn: async () => {
      const res = await api.get<{ data: PixChargeDto[] }>(`/wa/pix/charges/${conversationId}`);
      return res.data ?? [];
    },
    enabled: !!conversationId,
    refetchInterval: (query) => {
      const charges = query.state.data;
      if (Array.isArray(charges) && charges.some((c) => c.status === 'pending')) return 5000;
      return false;
    },
  });
}

export function useSendPixCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      inboxId: string;
      conversationId: string;
      to: string;
      amount: number;
      description?: string;
      expiresInHours?: number;
    }) => api.post<{ chargeId: string }>('/wa/pix/send', data),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['billing', 'pix', variables.conversationId] });
      qc.invalidateQueries({ queryKey: ['messages', variables.conversationId] });
    },
  });
}

export function useAdvanceOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (completedStep: OnboardingStep) =>
      api.patch<{ onboarding: OnboardingStatusDto }>('/me/onboarding-status', { completedStep }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'onboarding'] });
    },
  });
}

export function useOnboardingDismiss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/me/onboarding/dismiss', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'onboarding'] });
    },
  });
}

// ── BIL-702: Add-ons ──────────────────────────────────────────────────────────
export interface AddonCatalogItem {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: 'AGENT_SEATS' | 'PHONE_NUMBERS' | 'AI_TOKENS' | 'STORAGE';
  units: number;
  priceBrl: number; // centavos
  isActive: boolean;
}

export interface TenantAddonDto {
  id: string;
  addonId: string;
  quantity: number;
  status: 'ACTIVE' | 'CANCELLED';
  cancelAtPeriodEnd: boolean;
  activatedAt: string;
  cancelledAt: string | null;
}

export function useAddonsCatalog() {
  return useQuery<{ addons: AddonCatalogItem[] }>({
    queryKey: ['billing', 'addons', 'catalog'],
    queryFn: () => api.get('/billing/addons'),
    staleTime: 5 * 60_000, // catalog changes rarely
  });
}

export function useMyAddons() {
  return useQuery<{ addons: TenantAddonDto[] }>({
    queryKey: ['billing', 'addons', 'my'],
    queryFn: () => api.get('/billing/addons/my'),
  });
}

export function useSubscribeAddon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { addonId: string; quantity?: number }) =>
      api.post<TenantAddonDto>('/billing/addons/subscribe', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing', 'addons'] });
    },
  });
}

export function useCancelAddon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (addonId: string) => api.post(`/billing/addons/${addonId}/cancel`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing', 'addons'] });
    },
  });
}

// ── BIL-701: Proration preview ────────────────────────────────────────────────
export interface ProrationPreviewDto {
  currentPlan: PlanCode;
  newPlan: PlanCode;
  creditBrl: number;
  chargeBrl: number;
  netBrl: number;
  daysRemaining: number;
  daysInPeriod: number;
  effectiveDate: string;
}

export function useProrationPreview(newPlan: PlanCode | null) {
  return useQuery<ProrationPreviewDto>({
    queryKey: ['billing', 'proration-preview', newPlan],
    queryFn: () =>
      api.get<ProrationPreviewDto>(`/billing/subscription/proration-preview?newPlan=${newPlan}`),
    enabled: !!newPlan,
  });
}

// ── BIL-704: Invoices ─────────────────────────────────────────────────────────
export interface InvoiceDto {
  id: string;
  tenantId: string;
  billingPeriod: string;
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'VOID' | 'UNCOLLECTIBLE';
  subtotalBrl: string;
  discountBrl: string;
  taxBrl: number;
  totalBrl: string;
  prorationBrl: number;
  lineItems: Array<{
    description: string;
    type: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  pdfUrl: string | null;
  dueDate: string | null;
  paidAt: string | null;
  createdAt: string;
}

export function useInvoices() {
  return useQuery<{ invoices: InvoiceDto[] }>({
    queryKey: ['billing', 'invoices'],
    queryFn: () => api.get('/billing/invoices'),
  });
}
